import api from './api';

/** True when API body indicates the recipient already signed. */
export function isAlreadySignedPayload(payload) {
  if (payload == null) return false;
  if (typeof payload === 'string') {
    return /already\s+signed/i.test(payload);
  }
  if (typeof payload !== 'object') return false;
  if (payload.already_signed === true) return true;
  const nested = payload.data;
  if (nested && typeof nested === 'object' && nested.already_signed === true) return true;
  const message = String(payload.message || payload.error || '');
  return /already\s+signed/i.test(message);
}

/** Prefer nested `data` when present on signing API envelopes. */
export function extractSigningPayloadData(payload) {
  if (!payload || typeof payload !== 'object') return null;
  const nested = payload.data;
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    return nested;
  }
  return payload;
}

/**
 * Signing Service
 * Public-facing API endpoints for document signing.
 * Steps 1–3 require no auth. Steps 4–6 require the X-Signing-Token header.
 *
 * Flow:
 *   1. GET  /api/signing-info/{envelopeRecipientId}   — landing info
 *   2. POST /api/otp-send/{envelopeRecipientId}       — send OTP
 *   3. POST /api/otp-verify/{envelopeRecipientId}     — verify OTP → signing_token
 *   4. GET  /api/signing-data/{envelopeRecipientId}   — documents, fields, signatures
 *   5. POST /api/signing-submit/{envelopeRecipientId} — submit signed data
 *   6. POST /api/signing-decline/{envelopeRecipientId}— decline signing
 */

// ─── 1. Get Signing Info (Public) ────────────────────────────────────────────

/**
 * Get envelope + recipient info for the signing landing page.
 * @param {number|string} recipientId - envelopeRecipientId from the signing link
 * @returns {Promise} { success, data: { envelope_id, recipient, documents, already_signed, already_declined, otp_verified, ... } }
 */
export const getSigningInfo = async (recipientId) => {
  try {
    console.log('🔐 Fetching signing info for recipient:', recipientId);
    const response = await api.get(`/signing-info/${recipientId}`);
    console.log('✅ Signing info received:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Error fetching signing info:', error);
    throw error;
  }
};

// ─── 2. Send OTP (Public) ────────────────────────────────────────────────────

/**
 * Send (or re-send) a 6-digit OTP to the recipient's email.
 * @param {number|string} recipientId - envelopeRecipientId
 * @returns {Promise} { success, message, expires_in }
 */
export const sendOtp = async (recipientId) => {
  try {
    console.log('📧 Sending OTP for recipient:', recipientId);
    const response = await api.post(`/otp-send/${recipientId}`);
    console.log('✅ OTP sent:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Error sending OTP:', error);
    throw error;
  }
};

// ─── 3. Verify OTP (Public) ─────────────────────────────────────────────────

/**
 * Verify the 6-digit OTP code. Returns a signing_token on success.
 * @param {number|string} recipientId - envelopeRecipientId
 * @param {string} otpCode - 6-digit OTP entered by user
 * @returns {Promise} { success, message, data: { signing_token, expires_in } }
 */
export const verifyOtp = async (recipientId, otpCode) => {
  try {
    console.log('🔑 Verifying OTP for recipient:', recipientId);
    const response = await api.post(`/otp-verify/${recipientId}`, {
      otp_code: otpCode,
    });
    console.log('✅ OTP verified:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Error verifying OTP:', error);
    throw error;
  }
};

// ─── 4. Get Signing Data (Requires X-Signing-Token) ─────────────────────────

/**
 * Load documents, fields, and existing signatures for the signing UI.
 * @param {number|string} recipientId - envelopeRecipientId
 * @param {string} signingToken - token from verifyOtp response
 * @returns {Promise} { success, data: { documents, fields, existing_signatures, ... } }
 */
export const getSigningData = async (recipientId, signingToken) => {
  try {
    console.log('📄 Fetching signing data for recipient:', recipientId);
    const response = await api.get(`/signing-data/${recipientId}`, {
      headers: { 'X-Signing-Token': signingToken },
    });
    console.log('✅ Signing data received:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Error fetching signing data:', error);
    throw error;
  }
};

// ─── 4b. Get Signing Document File (Requires X-Signing-Token) ───────────────

/**
 * Fetch a document's PDF binary for the signing viewer.
 * @param {number|string} recipientId - envelopeRecipientId
 * @param {number|string} documentId - document ID
 * @param {string} signingToken - token from verifyOtp response
 * @returns {Promise<ArrayBuffer>} PDF file as ArrayBuffer
 */
export const getSigningDocumentFile = async (recipientId, documentId, signingToken) => {
  try {
    const response = await api.get(`/signing-document-file/${recipientId}/${documentId}`, {
      headers: { 'X-Signing-Token': signingToken },
      responseType: 'arraybuffer',
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching signing document file:', error);
    throw error;
  }
};

// ─── 4c. Get Signing Document Files Batch ZIP (Requires X-Signing-Token) ─────

/**
 * Fetch temporary ZIP URL(s) for multiple signing documents in one request.
 * Uses the signing-specific batch ZIP endpoint.
 * @param {number|string} recipientId - envelopeRecipientId
 * @param {Array<number|string>} documentIds - document IDs
 * @param {string} signingToken - token from verifyOtp response
 * @param {number} ttlMinutes - signed URL TTL
 * @returns {Promise<{items: Array, meta: Object}>}
 */
export const getSigningDocumentFileBatchZipUrls = async (recipientId, documentIds, signingToken, ttlMinutes = 30) => {
  const normalizedIds = (Array.isArray(documentIds) ? documentIds : [])
    .map((id) => String(id ?? '').trim())
    .filter(Boolean);

  if (normalizedIds.length === 0) {
    return {
      items: [],
      meta: {
        requested_count: 0,
        available_count: 0,
        missing_document_ids: [],
        ttl_minutes: ttlMinutes,
      },
    };
  }

  const parseBatchPayload = (response) => {
    const payload = response?.data || {};
    const payloadData = payload?.data;
    const items = Array.isArray(payloadData)
      ? payloadData
      : Array.isArray(payloadData?.items)
        ? payloadData.items
        : Array.isArray(payload?.items)
          ? payload.items
          : [];
    const meta = payload?.meta || payloadData?.meta || {};
    return { items, meta };
  };

  const headers = signingToken ? { 'X-Signing-Token': signingToken } : {};
  const response = await api.post(
    `/signing-document-files-batch-zip/${recipientId}`,
    { document_ids: normalizedIds, ttl_minutes: ttlMinutes },
    { headers, skipRequestDedup: true }
  );
  return parseBatchPayload(response);
};

// ─── 5. Submit Signing (Requires X-Signing-Token) ───────────────────────────

/**
 * Submit all field values and signature.
 * @param {number|string} recipientId - envelopeRecipientId
 * @param {string} signingToken - token from verifyOtp response
 * @param {Object} payload
 * @param {Object} payload.field_data - { field_{id}: value, ... }
 * @param {string} [payload.signature_type] - 'drawn' | 'uploaded' | 'existing'
 * @param {string} [payload.existing_signature_path] - reuse path from existing_signatures
 * @returns {Promise} { success, message, data: { signed_at, all_signees_completed, envelope_status } }
 */
export const submitSigning = async (recipientId, signingToken, payload) => {
  try {
    console.log('✍️ Submitting signing for recipient:', recipientId);
    const response = await api.post(`/signing-submit/${recipientId}`, payload, {
      headers: { 'X-Signing-Token': signingToken },
    });
    console.log('✅ Signing submitted:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Error submitting signing:', error);
    throw error;
  }
};

// ─── 6. Decline Signing (Requires X-Signing-Token) ──────────────────────────

/**
 * Decline signing the document.
 * @param {number|string} recipientId - envelopeRecipientId
 * @param {string} signingToken - token from verifyOtp response
 * @param {string} [reason] - optional decline reason
 * @returns {Promise} { success, message, data: { declined_at, envelope_status } }
 */
export const declineSigning = async (recipientId, signingToken, reason = '') => {
  try {
    console.log('🚫 Declining signing for recipient:', recipientId);
    const response = await api.post(
      `/signing-decline/${recipientId}`,
      { reason },
      { headers: { 'X-Signing-Token': signingToken } }
    );
    console.log('✅ Signing declined:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Error declining signing:', error);
    throw error;
  }
};
